import React from "react";
// CookieConsent type imported from CookieBanner
import { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Switch,
  Divider,
  Flex,
  Button,
  Text,
} from "@chakra-ui/react";
import { useContaxt } from "../../context/Context";

export function ConsentSettings({
  saveCookies,
  saveAllCookies,
  goBack,
}) {
  const { color } = useContaxt();

  const [cookieConsent, setCookieConsent] = useState({
    marketing: false,
    analytics: false,
    preferences: false,
    sale_of_data: false,
  });

  const cookieCategories = [
    {
      key: "necessary",
      title: "Notwendige Cookies",
      description: "Diese Cookies sind für das Funktionieren der Website erforderlich und können nicht deaktiviert werden.",
      required: true,
      enabled: true,
    },
    {
      key: "analytics",
      title: "Analyse-Cookies",
      description: "Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.",
      required: false,
      enabled: cookieConsent.analytics,
    },
    {
      key: "marketing",
      title: "Marketing-Cookies",
      description: "Diese Cookies werden verwendet, um dir relevante Werbung zu zeigen.",
      required: false,
      enabled: cookieConsent.marketing,
    },
    {
      key: "preferences",
      title: "Präferenz-Cookies",
      description: "Diese Cookies speichern deine Einstellungen und Präferenzen.",
      required: false,
      enabled: cookieConsent.preferences,
    },
  ];

  const handleToggle = (key, value) => {
    setCookieConsent((prev) => ({ ...prev, [key]: value }));
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      marketing: true,
      analytics: true,
      preferences: true,
      sale_of_data: true,
    };
    setCookieConsent(allAccepted);
    saveAllCookies();
  };

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="rgba(0, 0, 0, 0.4)"
      backdropFilter="blur(8px)"
      zIndex={10000}
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
    >
      <Box
        bg="rgba(12, 31, 44, 0.95)"
        borderRadius="20px"
        border={`3px solid ${color.mainColor}`}
        maxW="650px"
        w="full"
        maxH="90vh"
        overflowY="auto"
        p={8}
        boxShadow={`0 25px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px ${color.mainColor}20`}
      >
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <VStack spacing={3}>
            <Text
              fontSize="2xl"
              fontWeight="700"
              color={color.mainColor}
              lineHeight="1.2"
              textAlign="center"
            >
              🍪 Cookie-Einstellungen
            </Text>
            <Text
              fontSize="md"
              color="rgba(255, 255, 255, 0.85)"
              lineHeight="1.6"
              textAlign="center"
              maxW="500px"
            >
              Wähle aus, welche Cookies du zulassen möchtest. Du kannst diese Einstellungen jederzeit ändern.
            </Text>
          </VStack>

          <Divider borderColor={`${color.mainColor}40`} borderWidth="2px" />

          {/* Cookie Categories */}
          <VStack spacing={4} align="stretch">
            {cookieCategories.map((category) => (
              <Box
                key={category.key}
                p={5}
                bg={`${color.mainColor}10`}
                borderRadius="16px"
                border={`2px solid ${color.mainColor}30`}
                transition="all 0.3s ease"
                _hover={{
                  bg: `${color.mainColor}15`,
                  borderColor: `${color.mainColor}50`,
                  transform: "translateY(-2px)",
                  boxShadow: `0 8px 25px ${color.mainColor}20`
                }}
              >
                <Flex justify="space-between" align="start" mb={2}>
                  <VStack align="start" spacing={1} flex={1}>
                    <Text
                      fontSize="lg"
                      fontWeight="600"
                      color="white"
                      lineHeight="1.3"
                    >
                      {category.title}
                    </Text>
                    <Text
                      fontSize="sm"
                      textAlign={"left"}
                      color="rgba(255, 255, 255, 0.7)"
                      lineHeight="1.4"
                    >
                      {category.description}
                    </Text>
                  </VStack>
                  <Switch
                    isChecked={category.enabled}
                    isDisabled={category.required}
                    onChange={(e) =>
                      !category.required &&
                      handleToggle(
                        category.key,
                        e.target.checked
                      )
                    }
                    sx={{
                      '.chakra-switch__track': {
                        bg: category.enabled ? color.mainColor : `${color.secondButton}60`,
                        _checked: {
                          bg: color.mainColor,
                        },
                      },
                      '.chakra-switch__thumb': {
                        bg: 'white',
                      }
                    }}
                    size="lg"
                  />
                </Flex>
                {category.required && (
                  <Text
                    fontSize="xs"
                    textAlign={"left"}
                    color="rgba(255, 255, 255, 0.5)"
                    fontStyle="italic"
                  >
                    Immer aktiv
                  </Text>
                )}
              </Box>
            ))}
          </VStack>

          <Divider borderColor={`${color.mainColor}40`} borderWidth="2px" />

          {/* Action Buttons */}
          <VStack spacing={3}>
            <HStack spacing={4} w="full">
              <Button
                onClick={() => saveCookies(cookieConsent)}
                bg="transparent"
                color="white"
                fontWeight="500"
                fontSize="sm"
                h="50px"
                flex={1}
                border={`2px solid ${color.secondButton}`}
                _hover={{
                  bg: `${color.secondButton}20`,
                  borderColor: "white",
                  transform: "translateY(-2px)",
                  boxShadow: `0 6px 20px ${color.secondButton}30`
                }}
                transition="all 0.3s ease"
                borderRadius="12px"
              >
                Auswahl speichern
              </Button>
              <Button
                onClick={handleAcceptAll}
                bg={color.mainColor}
                _hover={{ 
                  bg: `${color.mainColor}E6`,
                  transform: "translateY(-2px)",
                  boxShadow: `0 6px 20px ${color.mainColor}40`
                }}
                color="white"
                fontWeight="600"
                fontSize="sm"
                h="50px"
                flex={1}
                transition="all 0.3s ease"
                borderRadius="12px"
              >
                Alle akzeptieren
              </Button>
            </HStack>
            <Button
              variant="ghost"
              color="rgba(255, 255, 255, 0.7)"
              fontSize="sm"
              onClick={goBack}
              _hover={{
                color: color.mainColor,
                bg: `${color.mainColor}15`,
                transform: "translateY(-1px)",
              }}
              transition="all 0.3s ease"
              fontWeight="500"
            >
              ← Zurück
            </Button>
          </VStack>
        </VStack>
      </Box>
    </Box>
  );
}
